export interface Signal<T> {
  get value(): T;
  subscribe(callback: (value: T) => void): () => void;
}
export interface WritableSignal<T> extends Signal<T> {
  set(value: T): void;
  update(fn: (current: T) => T): void;
}
export function signal<T>(initialValue: T): WritableSignal<T> {
  let _value = initialValue;
  const subscribers = new Set<(value: T) => void>();
  return {
    get value() {
      return _value;
    },
    set(newValue: T) {
      if (!Object.is(_value, newValue)) {
        _value = newValue;
        subscribers.forEach((fn) => fn(_value));
      }
    },
    update(fn: (current: T) => T) {
      this.set(fn(_value));
    },
    subscribe(callback: (value: T) => void): () => void {
      subscribers.add(callback);
      return () => subscribers.delete(callback);
    },
  };
}
export function computed<T>(
  fn: () => T,
  dependencies: Signal<any>[]
): Signal<T> {
  const result = signal<T>(fn());
  dependencies.forEach((dep) =>
    dep.subscribe(() => {
      const newValue = fn();
      if (!Object.is(result.value, newValue)) {
        (result as WritableSignal<T>).set(newValue);
      }
    })
  );
  return {
    get value() {
      return result.value;
    },
    subscribe(callback: (value: T) => void): () => void {
      return result.subscribe(callback);
    },
  };
}
export function effect(
  fn: () => void | (() => void),
  dependencies: Signal<any>[]
): () => void {
  let cleanup: (() => void) | void;
  const run = () => {
    if (cleanup) {
      cleanup();
      cleanup = undefined;
    }
    cleanup = fn();
  };
  const unsubscribes = dependencies.map((dep) => dep.subscribe(run));
  run();
  return () => {
    if (cleanup) {
      cleanup();
      cleanup = undefined;
    }
    unsubscribes.forEach((unsub) => unsub());
  };
}
